/**
 * Utility functions for testing internet connectivity
 * NOTE: Only runs on native mobile platforms (iOS/Android).
 * Web browsers already handle connectivity detection natively.
 */

import { Platform } from 'react-native';

export interface ConnectivityTestOptions {
  timeout?: number;
  testUrls?: string[];
  retries?: number;
}

const DEFAULT_TEST_URLS = [
  'https://www.google.com/favicon.ico',
  'https://www.cloudflare.com/favicon.ico',
];

/**
 * Test actual internet connectivity by attempting to fetch from multiple endpoints
 * Only runs on native platforms - web browsers handle this automatically
 */
export async function testInternetConnectivity(
  options: ConnectivityTestOptions = {}
): Promise<boolean> {
  // Skip connectivity checks on web - browser handles this natively
  if (typeof window === 'undefined' || Platform.OS === 'web') {
    return true;
  }

  const { timeout = 5000, testUrls = DEFAULT_TEST_URLS, retries = 1 } = options;

  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      // Test multiple endpoints for better reliability
      const testPromises = testUrls.map(url =>
        fetch(url, {
          method: 'GET',
          mode: 'no-cors',
          cache: 'no-cache',
          signal: AbortSignal.timeout(timeout),
        })
          .then(() => true)
          .catch(() => false)
      );

      // Use Promise.allSettled to get all results
      const results = await Promise.allSettled(testPromises);
      const hasConnection = results.some(
        result => result.status === 'fulfilled' && result.value === true
      );

      if (hasConnection) {
        return true;
      }

      // If this isn't the last attempt, wait before retrying
      if (attempt < retries) {
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    } catch (error) {
      console.warn(`Connectivity test attempt ${attempt + 1} failed:`, error);
    }
  }

  return false;
}

/**
 * Create a connectivity monitor that periodically checks connection status
 * Only runs on native platforms - no-op on web
 */
export function createConnectivityMonitor(
  onStatusChange: (isOnline: boolean) => void,
  options: ConnectivityTestOptions & { interval?: number } = {}
) {
  // On web, always report as online and return no-op functions
  if (Platform.OS === 'web') {
    return {
      start: () => () => {},
      stop: () => {},
      checkNow: () => {},
    };
  }

  const { interval = 30000, ...testOptions } = options;
  let intervalId: ReturnType<typeof setInterval>;
  let lastStatus: boolean | null = null;

  const checkConnectivity = async () => {
    const isOnline = await testInternetConnectivity(testOptions);

    if (lastStatus !== isOnline) {
      lastStatus = isOnline;
      onStatusChange(isOnline);
    }
  };

  const start = () => {
    // Initial check
    checkConnectivity();

    // Periodic checks
    intervalId = setInterval(checkConnectivity, interval);

    // Check on visibility change
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkConnectivity();
      }
    };

    // Check on focus
    const handleFocus = () => {
      checkConnectivity();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  };

  const stop = () => {
    if (intervalId) {
      clearInterval(intervalId);
    }
  };

  const checkNow = () => {
    checkConnectivity();
  };

  return { start, stop, checkNow };
}

/**
 * Hook for using connectivity monitoring in React components
 * Only performs checks on native platforms - always returns online on web
 */
import { useState, useEffect, useCallback } from 'react';

export function useConnectivityMonitor(
  options: ConnectivityTestOptions & { interval?: number } = {}
) {
  const [isOnline, setIsOnline] = useState(true);
  const [isChecking, setIsChecking] = useState(false);

  useEffect(() => {
    // Skip monitoring on web
    if (Platform.OS === 'web') {
      return;
    }

    const monitor = createConnectivityMonitor(setIsOnline, options);
    const cleanup = monitor.start();

    return cleanup;
  }, []);

  const checkNow = useCallback(async () => {
    // Skip check on web
    if (Platform.OS === 'web') {
      return;
    }

    setIsChecking(true);
    try {
      const status = await testInternetConnectivity(options);
      setIsOnline(status);
    } finally {
      setIsChecking(false);
    }
  }, [options]);

  return { isOnline, isChecking, checkNow };
}
